"use client";

import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  forwardRef,
  ChangeEvent,
} from "react";
import { IoLocationOutline } from "react-icons/io5";
import { FaGlobeAmericas } from "react-icons/fa";
import { HiOutlineLocationMarker } from "react-icons/hi";
import { IoMdClose } from "react-icons/io";
import useLocations, { Location, LocationScope } from "@/app/hooks/useLocations";

interface LocationAutocompleteProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onSelect"> {
  onSelect: (location: Location) => void;
  onClear?: () => void;
  placeholder?: string;
  className?: string;
  showClearButton?: boolean;
  maxSuggestions?: number;
  /** Which destination list to suggest from; courses by default. */
  scope?: LocationScope;
}

const LocationAutocomplete = forwardRef<HTMLInputElement, LocationAutocompleteProps>(
  (
    {
      onSelect,
      onClear,
      placeholder = "Search by city or country",
      className = "",
      showClearButton = true,
      maxSuggestions = 8,
      scope = "courses",
      value,
      onChange,
      ...props
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = useState(value?.toString() || "");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [debouncedInputValue, setDebouncedInputValue] = useState("");

    const { searchLocations } = useLocations(scope);

    // Debounce del input
    useEffect(() => {
      const timer = setTimeout(() => {
        setDebouncedInputValue(inputValue);
      }, 300);
      return () => {
        clearTimeout(timer);
      };
    }, [inputValue]);

    // Actualizar input value cuando cambie el prop value
    useEffect(() => {
      setInputValue(value?.toString() || "");
    }, [value]);

    const filteredSuggestions = useMemo(() => {
      return searchLocations(debouncedInputValue, maxSuggestions);
    }, [debouncedInputValue, searchLocations, maxSuggestions]);

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        setShowSuggestions(true);
        if (onChange) {
          onChange(e);
        }
      },
      [onChange],
    );

    const handleSuggestionClick = useCallback(
      (location: Location) => {
        setInputValue(location.displayName);
        setShowSuggestions(false);
        onSelect(location);
      },
      [onSelect],
    );

    const handleClear = useCallback(() => {
      setInputValue("");
      setShowSuggestions(false);
      if (onClear) {
        onClear();
      }
    }, [onClear]);

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        setTimeout(() => setShowSuggestions(false), 200);
        if (props.onBlur) {
          props.onBlur(e);
        }
      },
      [props],
    );

    const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        if (inputValue && filteredSuggestions.length > 0) {
          setShowSuggestions(true);
        }
        if (props.onFocus) {
          props.onFocus(e);
        }
      },
      [inputValue, filteredSuggestions.length, props],
    );

    const getLocationIcon = (type: Location['type']) => {
      if (type === 'country') {
        return <FaGlobeAmericas className="text-blue-500" size={16} />;
      }
      return <HiOutlineLocationMarker className="text-green-500" size={16} />;
    };

    return (
      <div className="relative">
        <div className="relative flex items-center">
          <IoLocationOutline 
            size={20} 
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 z-10" 
          />
          <input
            {...props}
            ref={ref}
            type="text"
            value={inputValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            placeholder={placeholder}
            className={`w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${className}`}
          />
          {showClearButton && inputValue && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer z-10"
            >
              <IoMdClose size={18} />
            </button>
          )}
        </div>

        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-auto">
            {filteredSuggestions.map((location) => (
              <div
                key={location.id}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSuggestionClick(location);
                }}
                className="flex items-center p-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                role="option"
                tabIndex={0}
              >
                <div className="mr-3 flex-shrink-0">
                  {getLocationIcon(location.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div 
                    className={`text-sm truncate ${
                      location.type === 'country' 
                        ? 'font-bold text-gray-900' 
                        : 'font-medium text-gray-800'
                    }`}
                  >
                    {location.type === 'country' ? location.name : location.displayName}
                  </div>
                  {location.type === 'country' && (
                    <div className="text-xs text-gray-500 mt-1">
                      Country
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    );
  },
);

LocationAutocomplete.displayName = "LocationAutocomplete";

export default LocationAutocomplete;